/**
 * @fileoverview FormInputs Component - Email and Password Input Fields for Login Form
 *
 * This component provides styled email and password input fields that integrate with
 * the TPA (Third Party App) settings system for dynamic styling. It supports comprehensive
 * customization through the design panel including borders, colors, spacing, and typography.
 *
 * @example
 * ```tsx
 * import { FormInputs } from './FormInputs';
 *
 * function LoginForm() {
 *   const [email, setEmail] = useState('');
 *   const [password, setPassword] = useState('');
 *
 *   return (
 *     <FormInputs
 *       email={email}
 *       password={password}
 *       onEmailChange={setEmail}
 *       onPasswordChange={setPassword}
 *     />
 *   );
 * }
 * ```
 *
 * @author SM Forms Team
 * @since 2024
 */

import React, {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { TextField, TextFieldTheme, ErrorAppearance } from 'wix-ui-tpa/cssVars';
import settingsParams from '../../../settingsParams';
import stylesParams from '../../../stylesParams';
import { BorderStyleOptions } from '../../../../common/Fields/BorderStyleOptions';
import { classes } from '../FormSection.st.css';
import { ForcedState, TEXT_DIRECTION } from '../../../../../constants';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

/**
 * Props for the FormInputs component
 */
interface FormInputsProps {
  /** Current email input value */
  email: string;
  /** Current password input value */
  password: string;
  /** Callback fired when email input changes */
  onEmailChange: (newEmail: string) => void;
  /** Callback fired when password input changes */
  onPasswordChange: (newPassword: string) => void;
  /** Whether the component is in editor mode */
  isEditor: boolean;
  /** Current forced state for editor preview */
  forceView?: ForcedState;
  /** Optional callback to expose validation function to parent */
  onValidationChange?: (isValid: boolean) => void;
  /** Optional callback to clear global errors when user starts typing */
  clearGlobalError?: () => void;
  /** Whether to auto-focus the email field on mount */
  autoFocus?: boolean;
}

/**
 * Validation result interface
 */
interface ValidationResult {
  isValid: boolean;
  emailError?: string;
  passwordError?: string;
}

/**
 * Imperative methods that can be called on FormInputs ref
 */
export interface FormInputsHandle {
  validate: () => ValidationResult;
}

/**
 * FormInputs Component
 *
 * Renders email and password input fields for the login form with comprehensive styling support.
 * This component integrates with the TPA settings system to apply dynamic styling including:
 * - Border styles (none, all sides, bottom only)
 * - Colors and opacity for borders and backgrounds
 * - Corner radius and spacing
 * - Font and text styling
 *
 * Features:
 * - Real-time style parameter validation with fallbacks
 * - Responsive design with flexible spacing
 * - Accessibility support with proper labels and data hooks
 * - Error handling for invalid style parameters
 * - Performance optimized with memoization to prevent unnecessary recalculations
 * - Memoized with React.memo to prevent re-renders when only button styles change
 *
 * Architecture Decision: Uses inline styles instead of CSS classes to avoid
 * TPA CSS specificity conflicts and ensure reliable style application.
 *
 * @param props - FormInputsProps containing email/password values and change handlers
 * @returns JSX element containing styled email and password input fields
 */
export const FormInputs = forwardRef<FormInputsHandle, FormInputsProps>(
  (
    {
      email,
      password,
      onEmailChange,
      onPasswordChange,
      isEditor,
      forceView,
      onValidationChange,
      clearGlobalError,
      autoFocus = false,
    },
    ref,
  ) => {
    // TPA Settings hooks for accessing design panel configurations
    const settings = useSettings();
    const styles = useStyles();
    const { isRTL } = useEnvironment();
    const { t } = useTranslation();

    // Error state management
    const [emailError, setEmailError] = React.useState<string | undefined>();
    const [passwordError, setPasswordError] = React.useState<
      string | undefined
    >();

    const emailFieldRef = useRef<React.ElementRef<typeof TextField>>(null);
    const passwordFieldRef = useRef<React.ElementRef<typeof TextField>>(null);

    useLayoutEffect(() => {
      if (autoFocus && !isEditor) {
        requestAnimationFrame(() => {
          emailFieldRef.current?.focus();
        });
      }
    }, [autoFocus, isEditor]);

    // Determine if we should show errors based on editor state or actual validation
    const shouldShowEmailError =
      (isEditor && forceView === ForcedState.INPUT_FIELDS_ERROR) || emailError;
    const shouldShowPasswordError =
      (isEditor && forceView === ForcedState.INPUT_FIELDS_ERROR) ||
      passwordError;
    // Determine if editor state is forcing error display for preview

    // Get the appropriate error messages
    const displayEmailError = shouldShowEmailError
      ? emailError ||
        t('memberAuthentication.uouScreens.loginForm.emailError.required')
      : undefined;
    const displayPasswordError = shouldShowPasswordError
      ? passwordError ||
        t('memberAuthentication.uouScreens.loginForm.passwordError.required')
      : undefined;

    // Validation function
    const validateEmail = (emailValue: string) => {
      if (!emailValue.trim()) {
        return t(
          'memberAuthentication.uouScreens.loginForm.emailError.required',
        );
      }
      if (!/\S+@\S+\.\S+/.test(emailValue)) {
        return t('memberAuthentication.uouScreens.loginForm.emailError.format');
      }
      return undefined;
    };

    const validatePassword = (passwordValue: string) => {
      if (!passwordValue.trim()) {
        return t(
          'memberAuthentication.uouScreens.loginForm.passwordError.required',
        );
      }
      return undefined;
    };

    // Handle email change with validation
    const handleEmailChange = (newEmail: string) => {
      onEmailChange(newEmail);
      // Clear field error when user starts typing
      if (emailError) {
        setEmailError(undefined);
      }
      // Clear global error when user starts typing after login error
      if (clearGlobalError) {
        clearGlobalError();
      }
    };
    // Expose validation method through ref
    useImperativeHandle(
      ref,
      () => ({
        validate: (): ValidationResult => {
          const emailValidationError = validateEmail(email);
          const passwordValidationError = validatePassword(password);

          // Update error states immediately
          setEmailError(emailValidationError);
          setPasswordError(passwordValidationError);

          const isValid = !emailValidationError && !passwordValidationError;

          // Focus the first field with an error
          if (!isValid) {
            if (emailValidationError) {
              emailFieldRef.current?.focus();
            } else if (passwordValidationError) {
              passwordFieldRef.current?.focus();
            }
          }

          return {
            isValid,
            emailError: emailValidationError,
            passwordError: passwordValidationError,
          };
        },
      }),
      [email, password],
    );

    // Handle password change with validation
    const handlePasswordChange = (newPassword: string) => {
      onPasswordChange(newPassword);
      if (passwordError) {
        setPasswordError(undefined);
      }
      if (clearGlobalError) {
        clearGlobalError();
      }
    };

    // Handle email blur (validate on blur)
    const handleEmailBlur = () => {
      const error = validateEmail(email);
      setEmailError(error);
    };

    // Handle password blur (validate on blur)
    const handlePasswordBlur = () => {
      const error = validatePassword(password);
      setPasswordError(error);
    };

    // Notify parent about validation state changes
    React.useEffect(() => {
      if (onValidationChange) {
        const isValid =
          !emailError && !passwordError && !!email.trim() && !!password.trim();
        onValidationChange(isValid);
      }
    }, [emailError, passwordError, email, password, onValidationChange]);

    // Text field labels and placeholders from settings
    const textFieldContent = {
      emailLabel: settings.get(settingsParams.emailLabelText),
      emailPlaceholder: settings.get(settingsParams.emailPlaceholderText),
      passwordLabel: settings.get(settingsParams.passwordLabelText),
      passwordPlaceholder: settings.get(settingsParams.passwordPlaceholderText),
    };

    const getTextFieldClassName = () => {
      const baseClass = classes.textField;
      const isHoverForced =
        isEditor && forceView === ForcedState.INPUT_FIELDS_HOVER;

      return isHoverForced
        ? `${baseClass} ${classes.textFieldHover}`
        : baseClass;
    };

    const theme =
      styles.get(stylesParams.inputFieldBorderStyle) ===
      BorderStyleOptions.BOTTOM
        ? TextFieldTheme.Line
        : TextFieldTheme.Box;

    return (
      <div className={classes.formInputs} data-hook="login-form-inputs">
        {/* Email Input Field */}
        <TextField
          ref={emailFieldRef}
          data-hook="login-email-input" // Test automation hook
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          onBlur={handleEmailBlur}
          label={textFieldContent.emailLabel}
          placeholder={textFieldContent.emailPlaceholder}
          type="email" // HTML5 email validation and mobile keyboard optimization
          className={getTextFieldClassName()} // Use CSS class for consistent styling
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
          theme={theme}
          newErrorMessage
          error={!!displayEmailError}
          errorMessage={displayEmailError}
          dir={isRTL ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR}
          aria-label={t(
            'memberAuthentication.ariaLabel.uouScreens.loginForm.emailLabel',
          )}
        />

        {/* Password Input Field */}
        <TextField
          ref={passwordFieldRef}
          data-hook="login-password-input" // Test automation hook
          value={password}
          onChange={(e) => handlePasswordChange(e.target.value)}
          onBlur={handlePasswordBlur}
          label={textFieldContent.passwordLabel}
          placeholder={textFieldContent.passwordPlaceholder}
          type="password" // Secure password input with masking
          className={getTextFieldClassName()} // Use CSS class for consistent styling
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
          theme={theme} // Apply theme based on border style
          newErrorMessage
          error={!!displayPasswordError}
          errorMessage={displayPasswordError}
          dir={isRTL ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR}
          aria-label={t(
            'memberAuthentication.ariaLabel.uouScreens.loginForm.passwordField',
          )}
          showPasswordTooltipText={t('app.widget.modals.show-password-tooltip')}
          hidePasswordTooltipText={t('app.widget.modals.hide-password-tooltip')}
        />
      </div>
    );
  },
);

FormInputs.displayName = 'FormInputs';
